/**
 * Scene 構築（happa v1.1）。
 *
 * `buildScene(L, state)` は `Layout + AppState` から描画に必要な幾何
 * （innerPoly + Cell 配列）を一括生成する pure 関数。spec §3.3 の二相
 * レンダリングモデルに従い、progress には依存しない。変位は描画時に
 * `cell.vx * easeOutExpo(progress)` として適用される。
 *
 * 主な処理:
 * 1. `generateInitialPolygon` で正多角形 + shape jitter
 * 2. Adaptive phantom offset を `avgCellRadius × 1.4` でクランプ（spec §3.4）
 * 3. 均等分布の Poisson-disk sampling（`generatePoissonDiskPointsInPolygon`）
 *    + 標準 Lloyd 3 回 + Cell Jitter（`FRAGMENT_JITTER_PCT = 50` 固定、Phase J）。
 * 4. Phantom ring と合成して d3-delaunay で Voronoi 分割
 *    clip rect は `computeVoronoiClipRect(innerPoly, phantomOffset)` で
 *    innerPoly AABB + `phantomOffset × 1.2` に絞る（Bug-2 対策）
 * 5. 各点 → Cell へ畳み込む。cell.vx / vy は **radial 連続線形カーブ**
 *    （Phase K1）で計算。`t = r / rMax` に応じて innerMax から
 *    innerMax × perimMult まで線形補間。旧 `shrunkPoly` による binary 分類
 *    （`isInner`）は撤去済み。`Scene.cells` には phantom 含む全 cell を格納し、
 *    描画対象の filter は render 層の責務。
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.3 / §3.4 / §3.7 / §5.3
 * - wireframes/v0.10/wireframe.html L558–620（`buildScene`）
 * - Phase K1: 破片 displacement の radial 連続化
 */

import { Delaunay } from 'd3-delaunay';

import {
  clampVec2,
  generateInitialPolygon,
  polygonAABB,
  polygonArea,
  polygonMaxRadius,
} from '../core/polygon';
import { createRng } from '../core/rng';
import type { Cell, Layout, Scene, Vec2 } from '../core/types';
import {
  computeVoronoiClipRect,
  generatePhantomRing,
  generatePoissonDiskPointsInPolygon,
  LLOYD_ITERATIONS,
  lloydInPolygon,
} from '../core/voronoi';
import type { AppState } from '../state';

/**
 * Cell jitter の固定振幅（%、Phase J F で `state.randomness` を撤去して
 * ハードコードに置換、旧 default 50 と同値）。spec §5.3:
 *   jAmp = (FRAGMENT_JITTER_PCT / 100) × innerShort × 0.02。
 * ユーザ調整が必要になったら再度 state へ昇格させる。
 */
const FRAGMENT_JITTER_PCT = 50;

/**
 * `Layout + AppState` から 1 フレーム分の Scene を構築する（spec §5.3）。
 *
 * 呼び出し側は `state.sceneCache` にキャッシュして progress 変更のたびに
 * 再計算しない（Q2 確定）。再構築トリガは `computeInvalidation` の
 * `layout | sceneShape | sceneVoronoi | cellMotion` フラグ。
 *
 * @param L    現在の Layout（`computeLayout(state)` の結果）。
 * @param state 現在の AppState（shape / fragmentation / motion を参照）。
 * @returns `{ innerPoly, cells }` からなる Scene。
 */
export function buildScene(L: Layout, state: AppState): Scene {
  // Voronoi 用 RNG の主系（jitter / noise / variance / rot をすべてこの単一
  // インスタンスで消費する）。polygon 側は generateInitialPolygon 内で
  // seed + 54321 のオフセット RNG を別途生成する（spec §3.2 / Q9）。
  const rng = createRng(state.seed);

  // ── 1. 正多角形 + shape jitter ────────────────────────────
  // Phase J（B）: silhouette を aspect 非依存にするため、`generateInitialPolygon`
  // に渡す inner 幅・高さを **`innerShort` の正方形**で固定する。結果として
  // silhouette は aspect 比に関係なく正方形領域内に内接する形（shapeScale に
  // より半径が決まる円/多角形）になる。16:9 / 9:16 / 4:3 いずれでも idle 状態
  // の見た目は 1:1 と一致する。破片が「空いた長辺方向」に飛ぶかは Phase J C
  // の Detonate aspect 比対応で制御する。
  const innerCX = (L.innerX0 + L.innerX1) / 2;
  const innerCY = (L.innerY0 + L.innerY1) / 2;
  const innerPoly = generateInitialPolygon({
    vertexCount: state.vertexCount,
    shapeRandomness: state.shapeRandomness,
    seed: state.seed,
    centerX: innerCX,
    centerY: innerCY,
    innerW: L.innerShort,
    innerH: L.innerShort,
    shapeScale: state.shapeScale,
  });

  // ── 2. Adaptive phantom offset（spec §3.4） ────────────────
  // avgCellRadius = sqrt(area / count / π) に 1.4 を掛け、
  // [innerShort * 0.04, innerShort * 0.25] でクランプ。破片数 20 でも
  // 外側 phantom が innerPoly をしっかり覆うため Inner 漏れを防ぐ。
  const area = polygonArea(innerPoly);
  const avgCellRadius = Math.sqrt(area / Math.max(1, state.fragmentCount) / Math.PI);
  const phantomOffset = Math.min(
    L.innerShort * 0.25,
    Math.max(L.innerShort * 0.04, avgCellRadius * 1.4),
  );

  // ── 3. Seed 点: Poisson-disk → Lloyd → Cell Jitter ─────────
  // Phase E Step 1 cleanup: 旧 generateWeightedPointsInPolygon を撤去し、
  // 均等分布 Poisson-disk + 標準 Lloyd に戻した。Phase J（F）: `randomness`
  // パラメータを state から撤去したため、jitter 振幅は以下のハードコード値
  // `FRAGMENT_JITTER_PCT` で固定する。旧 default（50）と同等の見た目を保つ。
  let realPoints = generatePoissonDiskPointsInPolygon({
    polygon: innerPoly,
    count: state.fragmentCount,
    seed: state.seed,
  });
  realPoints = lloydInPolygon(realPoints, innerPoly, LLOYD_ITERATIONS);
  // spec §5.3: jAmp = (FRAGMENT_JITTER_PCT / 100) × innerShort × 0.02。
  const jAmp = (FRAGMENT_JITTER_PCT / 100) * L.innerShort * 0.02;
  realPoints = realPoints.map(
    ([x, y]: Vec2): Vec2 => [x + (rng() - 0.5) * jAmp, y + (rng() - 0.5) * jAmp],
  );

  // ── 5. Phantom ring + Voronoi 境界（spec §5.3, Bug-2 対策） ──
  const phantoms = generatePhantomRing({
    polygon: innerPoly,
    offset: phantomOffset,
    ringDensity: 6,
  });
  const allPoints: Vec2[] = [...realPoints, ...phantoms];
  const delaunay = Delaunay.from(allPoints);
  // Bug-2: v0 の broad clip（outerW/H）を、innerPoly AABB + phantomOffset × 1.2 に
  // 絞る。Voronoi セル頂点の過剰伸長を抑制する。
  const voronoi = delaunay.voronoi(computeVoronoiClipRect(innerPoly, phantomOffset));

  // ── 6. Cell 構築（real + phantom 両方、spec §3.7 / Bug-2 対策） ──
  // Bug-2: cell 頂点を innerPoly AABB で clamp して過剰伸長を封じる。
  // Scene.cells には phantom 由来も含め全 cell を格納し、render 層が
  // `cell.isPhantom === false` で filter する責務（spec §3.4 / Cell 型参照）。
  const innerAABB = polygonAABB(innerPoly);
  return {
    innerPoly,
    cells: buildCells(
      allPoints,
      realPoints.length,
      voronoi,
      innerPoly,
      innerAABB,
      L,
      state,
      rng,
      innerCX,
      innerCY,
    ),
  };
}

/**
 * allPoints（real + phantom）の各 index に対し Voronoi セルを取り出して Cell に
 * 畳み込む。`i < realPointsCount` なら `isPhantom: false`、それ以外は `true`。
 *
 * 関数分離の動機: `buildScene` 本体の認知的複雑度を Biome 制限（20）以下に
 * 抑えるため、Cell 1 個分の畳み込みロジックを別関数に切り出す。
 *
 * cell 頂点は `innerAABB` で clamp する（Bug-2 対策）。Voronoi clip rect で
 * ある程度抑えているが、clamp を追加することで下流の warpPolygonByCells 等で
 * 過剰座標が漏れ出すのを確実に防ぐ。
 *
 * **Phase K1**: 旧 `shrunkPoly` による Inner/Perimeter binary 分類を撤去し、
 * 中心からの radial 距離 `t = r / rMax` に基づく線形カーブに置換。`rMax` は
 * `polygonMaxRadius(innerPoly, center)` から 1 度だけ求めて全 cell で共有する
 * （ホットパス化の下準備）。degenerate（rMax <= 0）は `buildOneCell` 側で
 * `t = 0` にフォールバックする。
 *
 * **Phase E Step 1 cleanup**: 旧 sizeCoeff / directionNoise / rotation の
 * 渡しを撤去。sizeCoeff は Cell 型から optional で残るが常に undefined、
 * 速度方向は radial のみ（ジッター無し）、cell.rot は 0（無回転飛散）。
 */
function buildCells(
  allPoints: Vec2[],
  realPointsCount: number,
  voronoi: ReturnType<Delaunay<Delaunay.Point>['voronoi']>,
  innerPoly: Vec2[],
  innerAABB: [number, number, number, number],
  L: Layout,
  state: AppState,
  rng: () => number,
  innerCX: number,
  innerCY: number,
): Cell[] {
  const V = state.speedVariance / 100;
  const innerMax = L.innerShort * (state.innerDispPct / 100);
  // Phase K1: radial カーブの分母 = innerPoly の最遠頂点距離。shapeScale や
  // shape jitter を反映した「理論上の境界半径」で t を正規化する。
  const rMax = polygonMaxRadius(innerPoly, [innerCX, innerCY]);
  // Phase J（C）: Aspect 非依存 silhouette（Step B）で polygon が正方形領域内
  // に収まるため、爆破時の飛散距離を aspect 比で横/縦に引き伸ばして、空いた
  // 長辺方向に破片が流れるようにする。`aspectX = innerW / innerShort`,
  // `aspectY = innerH / innerShort` のいずれかが 1.0、もう一方が >= 1.0 に
  // なる（innerShort は min(innerW, innerH) の定義より）。
  // 1:1 → (1, 1)、16:9 → (1.78, 1)、9:16 → (1, 1.78)、4:3 → (1.33, 1)。
  const aspectX = L.innerW / L.innerShort;
  const aspectY = L.innerH / L.innerShort;

  const cells: Cell[] = [];
  for (let i = 0; i < allPoints.length; i++) {
    const polyRaw = voronoi.cellPolygon(i);
    if (!polyRaw) continue;
    const seed = allPoints[i];
    if (!seed) continue;
    const clampedPoly = (polyRaw as Vec2[]).map((v) => clampVec2(v, innerAABB));
    const isPhantom = i >= realPointsCount;
    const cell = buildOneCell(seed, clampedPoly, isPhantom, {
      innerMax,
      perimMult: state.perimMult,
      V,
      rng,
      innerCX,
      innerCY,
      rMax,
      aspectX,
      aspectY,
    });
    cells.push(cell);
  }
  return cells;
}

/**
 * 1 つの seed 点 → 1 Cell（速度ベクトル + 回転）を生成する。`buildCells` から
 * 各反復で呼ばれる。
 *
 * Phase K1（radial 連続化）: 旧 binary 分類（`isInner ? innerMax : innerMax ×
 * perimMult`）が `shrunkPoly` 境界で `perimMult` 倍の不連続を起こし、
 * 「2 重円」状の空白帯を生んでいた問題を解消するため、中心からの radial 位置
 * `t = r / rMax`（0..1）で `innerMax` と `innerMax × perimMult` の間を
 * **線形補間**する:
 *
 * ```
 * maxDisp = innerMax × (1 + t × (perimMult − 1))
 *         = lerp(innerMax, innerMax × perimMult, t)
 * ```
 *
 * ユーザ向け param の意味合い:
 * - `innerDispPct`  … **中心**での基準変位（`innerMax` = innerShort × %/100）
 * - `perimMult`     … **境界** (t=1) での倍率
 * - `perimMult = 1.0` → 全破片が一律に innerMax
 * - `perimMult < 1.0` → 中心のほうが遠くへ飛ぶ（逆相、珍しい挙動）
 *
 * `isPhantom === true` の cell も（描画はされないが）速度ベクトル等を
 * 計算しておく。理由:
 * 1. phantom の有無で rng() の消費順を分けると、realPoints 側の決定論が揺れる
 * 2. 将来 phantom cell を debug 表示する等の用途で、Cell 形を崩さないほうが
 *    取り回しやすい
 */
function buildOneCell(
  seed: Vec2,
  poly: Vec2[],
  isPhantom: boolean,
  params: {
    innerMax: number;
    perimMult: number;
    V: number;
    /** Phase K1: innerPoly の中心からの最遠頂点距離。t の分母。 */
    rMax: number;
    /** Phase J C: 飛散距離の横方向スケール。innerW / innerShort。 */
    aspectX: number;
    /** Phase J C: 飛散距離の縦方向スケール。innerH / innerShort。 */
    aspectY: number;
    rng: () => number;
    innerCX: number;
    innerCY: number;
  },
): Cell {
  const [px, py] = seed;
  const { innerMax, perimMult, V, rMax, rng, innerCX, innerCY, aspectX, aspectY } = params;

  // 中心 → seed 方向の単位ベクトル + 距離。dist=0 のときはゼロ除算回避で 1 を
  // 代入（t も 0 にフォールバック、下で明示）。
  // Phase E Step 1 cleanup: directionNoise 撤去により radial 方向のみ使用。
  const dx = px - innerCX;
  const dy = py - innerCY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const safeDist = dist > 0 ? dist : 1;
  const vxDir = dx / safeDist;
  const vyDir = dy / safeDist;

  // speed variance: 1 ± V の範囲で速度をスケール。rng() は 1 本残す必要がある
  // （direction noise の rng 消費が消えたので決定論も変わる点に注意）。
  const spd = 1 - V + rng() * 2 * V;

  // Phase K1: radial 線形カーブで maxDisp を決める。`t` は中心で 0、境界で 1
  // （dist > rMax の微小な浮動小数誤差は clamp で吸収）。rMax <= 0 の退化
  // （空 polygon 等）では t=0 にフォールバックし、maxDisp = innerMax。
  // `lerp(innerMax, innerMax × perimMult, t)` と等価。
  const t = rMax > 0 ? Math.min(1, dist / rMax) : 0;
  const maxDisp = innerMax * (1 + t * (perimMult - 1));

  // Phase J C: 飛散距離を aspect で非等方にスケール。radial 方向に maxDisp ×
  // spd を掛けた値に、横成分には aspectX、縦成分には aspectY を乗算する。
  // 16:9 → 横方向だけ 1.78 倍、9:16 → 縦方向だけ 1.78 倍。1:1 では (1, 1)
  // なので変化なし（Phase I 以前の挙動と一致）。
  // Phase E Step 1 cleanup: rot は 0 固定（無回転飛散）。Cell.rot は contract
  // として残すが、scene 構築時に 0 を代入し、transformPoly 側の rot*p*0.25 計算
  // は no-op になる。
  const cell: Cell = {
    points: poly,
    centroid: [px, py],
    vx: vxDir * maxDisp * spd * aspectX,
    vy: vyDir * maxDisp * spd * aspectY,
    rot: 0,
    isPhantom,
  };
  return cell;
}
